/**
 * Advanced Subscription Frequency Selector
 * Allows customers to choose delivery frequency for their wine subscriptions
 */

class VinpopFrequencySelector {
  constructor() {
    this.frequencies = [
      { 
        id: 'monthly', 
        value: '30', 
        translationKey: 'subscription.frequency_monthly',
        popular: true 
      },
      { 
        id: 'bimonthly', 
        value: '60', 
        translationKey: 'subscription.frequency_bimonthly'
      },
      { 
        id: 'quarterly', 
        value: '90', 
        translationKey: 'subscription.frequency_quarterly'
      }
    ];
    
    this.selectedFrequency = 'monthly'; // Default selection
    this.currentLang = this.detectLanguage();
  }

  detectLanguage() {
    const htmlLang = document.documentElement.lang || 'en';
    return ['en', 'nl', 'de', 'fr'].includes(htmlLang) ? htmlLang : 'en';
  }

  getTranslation(key, fallback = '') {
    // Try to get Shopify translation first
    if (window.theme && window.theme.strings && window.theme.strings[key]) {
      return window.theme.strings[key];
    }
    
    // Fallback translations for development/testing
    const translations = {
      'subscription.frequency_title': {
        en: 'How often would you like to receive your wine box?',
        nl: 'Hoe vaak wilt u uw wijnbox ontvangen?',
        de: 'Wie oft möchten Sie Ihre Weinbox erhalten?',
        fr: 'À quelle fréquence souhaitez-vous recevoir votre box vin?'
      },
      'subscription.frequency_monthly': {
        en: 'Every month',
        nl: 'Elke maand',
        de: 'Jeden Monat',
        fr: 'Chaque mois'
      },
      'subscription.frequency_bimonthly': {
        en: 'Every 2 months',
        nl: 'Elke 2 maanden',
        de: 'Alle 2 Monate',
        fr: 'Tous les 2 mois'
      },
      'subscription.frequency_quarterly': {
        en: 'Every 3 months',
        nl: 'Elke 3 maanden',
        de: 'Alle 3 Monate',
        fr: 'Tous les 3 mois'
      },
      'subscription.popular_badge': {
        en: 'Popular',
        nl: 'Populair', 
        de: 'Beliebt',
        fr: 'Populaire'
      }
    };
    
    return translations[key]?.[this.currentLang] || translations[key]?.en || fallback;
  }

  createSelector() {
    const container = document.createElement('div');
    container.className = 'vinpop-frequency-selector';
    container.innerHTML = `
      <div class="frequency-title">
        <h3>${this.getTranslation('subscription.frequency_title')}</h3>
      </div>
      <div class="frequency-options">
        ${this.frequencies.map(freq => `
          <label class="frequency-option ${freq.popular ? 'popular' : ''}" data-frequency="${freq.id}">
            <input type="radio" name="delivery_frequency" value="${freq.value}" ${freq.id === this.selectedFrequency ? 'checked' : ''}>
            <div class="option-content">
              <span class="option-label">${this.getTranslation(freq.translationKey)}</span>
              ${freq.popular ? `<span class="popular-badge">${this.getTranslation('subscription.popular_badge')}</span>` : ''}
            </div>
          </label>
        `).join('')}
      </div>
    `;

    this.addStyles();
    this.attachEvents(container);
    return container;
  }

  addStyles() {
    if (document.getElementById('vinpop-frequency-styles')) return;
    
    const styles = document.createElement('style');
    styles.id = 'vinpop-frequency-styles';
    styles.textContent = `
      .vinpop-frequency-selector {
        margin: 1.5rem 0;
        padding: 1.5rem;
        background: #f8f9fa;
        border-radius: 0.75rem;
        border: 2px solid #e9ecef;
      }

      .frequency-title h3 {
        margin: 0 0 1rem 0;
        font-size: 1.1rem;
        font-weight: 600;
        color: #374151;
        text-align: center;
      }

      .frequency-options {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .frequency-option {
        position: relative;
        display: block;
        cursor: pointer;
        border-radius: 0.5rem;
        overflow: hidden;
        transition: all 0.2s ease;
        background: white;
        border: 2px solid #e5e7eb;
      }

      .frequency-option:hover {
        border-color: #a0ced9;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(160, 206, 217, 0.2);
      }

      .frequency-option.popular {
        border-color: #ffcc37;
        position: relative;
      }

      .frequency-option.popular::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #ffcc37, #a0ced9);
        z-index: 1;
      }

      .frequency-option input[type="radio"] {
        opacity: 0;
        position: absolute;
        width: 100%;
        height: 100%;
        margin: 0;
        cursor: pointer;
      }

      .frequency-option input[type="radio"]:checked + .option-content {
        background: #a0ced9;
        color: #000;
        font-weight: 600;
      }

      .frequency-option input[type="radio"]:checked + .option-content::after {
        content: '✓';
        position: absolute;
        top: 50%;
        right: 0.75rem;
        transform: translateY(-50%);
        font-size: 1.2rem;
        font-weight: bold;
        color: #000;
      }

      .option-content {
        position: relative;
        padding: 0.625rem 0.75rem;
        text-align: center;
        background: white;
        transition: all 0.2s ease;
        min-height: 45px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 0.25rem;
      }

      .option-label {
        font-size: 0.95rem;
        font-weight: 500;
        color: #374151;
      }

      .popular-badge {
        background: #ffcc37;
        color: #000;
        padding: 0.2rem 0.5rem;
        border-radius: 1rem;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .frequency-option input[type="radio"]:checked + .option-content .option-label {
        color: #000;
      }

      .frequency-option input[type="radio"]:checked + .option-content .popular-badge {
        background: #000;
        color: #ffcc37;
      }

      /* Mobile responsive */
      @media (max-width: 768px) {
        .frequency-options {
          grid-template-columns: 1fr;
        }
        
        .vinpop-frequency-selector {
          margin: 1rem 0;
          padding: 1rem;
        }
        
        .frequency-title h3 {
          font-size: 1rem;
        }
      }
    `;
    
    document.head.appendChild(styles);
  }

  attachEvents(container) {
    const options = container.querySelectorAll('input[name="delivery_frequency"]');
    options.forEach(option => {
      option.addEventListener('change', (e) => {
        this.selectedFrequency = e.target.closest('.frequency-option').dataset.frequency;
        this.updateSubscriptionButton();
        console.log('🔄 Frequency changed to:', this.selectedFrequency, 'days:', e.target.value);
      });
    });
  }

  updateSubscriptionButton() {
    const subscriptionButton = document.querySelector('.vinpop-subscribe-button');
    if (subscriptionButton) {
      const selectedOption = this.frequencies.find(f => f.id === this.selectedFrequency);
      if (selectedOption) {
        // Update button text to include frequency
        const baseText = this.currentLang === 'nl' ? 'Abonneren en besparen 10%' : 'Subscribe and Save 10%';
        const frequencyText = this.getTranslation(selectedOption.translationKey);
        
        // Update the button's data attribute for form submission
        subscriptionButton.setAttribute('data-frequency', selectedOption.value);
        
        console.log('📝 Updated subscription button with frequency:', frequencyText);
      }
    }
  }

  getSelectedFrequency() {
    const selectedFreq = this.frequencies.find(f => f.id === this.selectedFrequency);
    return {
      id: this.selectedFrequency,
      value: selectedFreq?.value,
      label: selectedFreq ? this.getTranslation(selectedFreq.translationKey) : ''
    };
  }
}

// Initialize frequency selector globally
window.VinpopFrequencySelector = VinpopFrequencySelector;
